import * as joda from "@js-joda/core";
import * as c from "purify-ts/Codec";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dyn, dynClass, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import { textbox } from "trader.ts/ui.common";
import { checkAllCasesHandled } from "trader.ts/utils";
import { messageid, studentid, teacherid } from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeClientsideRouter } from "../../client/clientsiderouter";
import { debounce } from "../../lib/debounce";
import { dateFormatWithYear } from "../../lib/time";
import { HHmm } from "../../render";
import * as routes from "../../routes";

// !!
// TODO:
// Omdat we nu aparte pagina's hebben gemaakt voor studenten hun berichten
// mag eigenlijk (een deel van) die "own_identity" stuff er uit

export type conversation = {
  counterparty_admin: boolean;
  counterparty_teacherid: teacherid | null;
  counterparty_teachername: string | null;
  counterparty_studentid: studentid | null;
  counterparty_studentname: string | null;
  last_message_stamp: joda.LocalDateTime | null;
  unread_messages_count: number;
  own_identity: counterparty;
};

export const conversationsSoftLimit = 30;

export type counterparty =
  | {
      tag: "Admin";
    }
  | { tag: "Teacher"; teacherId: teacherid }
  | { tag: "Student"; studentId: studentid; name: string };
type mode =
  | { tag: "none" }
  | {
      tag: "selected";
      own_identity: counterparty;
      counterparty: counterparty;
    }
  | { tag: "search" };

export const counterpartyC = c.oneOf([
  c.Codec.interface({ tag: c.exactly("Admin") }),
  c.Codec.interface({
    tag: c.exactly("Teacher"),
    teacherId: routes.newtypeCodec<teacherid>(),
  }),

  c.Codec.interface({
    tag: c.exactly("Student"),
    studentId: routes.newtypeCodec<studentid>(),
    name: c.string,
  }),
]);

const modeC: c.Codec<mode> = c.oneOf([
  c.Codec.interface({ tag: c.exactly("none") }),
  c.Codec.interface({ tag: c.exactly("search") }),
  c.Codec.interface({
    tag: c.exactly("selected"),
    own_identity: counterpartyC,
    counterparty: counterpartyC,
  }),
]);

const modeRoute = makeRoute("/mode/{mode:mode}", {
  mode: makeEncoderFromCodec(modeC),
});
const router = makeClientsideRouter(modeRoute, { tag: "none" });

export function render(opts: {
  schoolname: string;
  preselect: boolean;
  conversations_: conversation[];
  roleTag: "Teacher" | "Admin";
  logged_in_email: string;
}): HTMLElement | HTMLElement[] {
  const conversations = opts.conversations_.map((c) => ({
    ...c,
    unreadCountS: new Source(c.unread_messages_count),
  }));
  const modeS = router.start();
  const initiallySelected = opts.preselect ? conversations[0] : null;

  if (initiallySelected) {
    void selectConversation(initiallySelected);
  }

  async function selectConversation(
    c: conversation & { unreadCountS: Source<number> },
    force?: true,
  ) {
    c.unreadCountS.set(0);
    document.location.hash =
      router.makeHashLink({
        tag: "selected",
        own_identity: c.own_identity,
        counterparty: conversationToCounterparty(c),
      }) + (force === true ? "?force=" + Math.floor(Math.random() * 1000) : "");
  }

  const showLoadMoreButton = conversations.length === conversationsSoftLimit;

  const shouldShowOwnIdentity =
    conversations
      .map((c) =>
        c.own_identity.tag === "Admin"
          ? "Admin"
          : c.own_identity.tag === "Teacher"
          ? "Teacher" + c.own_identity.teacherId
          : c.own_identity.tag === "Student"
          ? "Student" + c.own_identity.studentId
          : checkAllCasesHandled(c.own_identity),
      )
      .compact()
      .uniq().length > 1;

  return dynClass(
    modeS,
    (mode) =>
      mode.tag === "none" ? "" : style.listleftlayout_main_with_selection,
    <div className={style.listleftlayout_main_}>
      <aside className={style.listleftlayout_left}>
        <div className={style.listleftlayout_left_container}>
          <a
            href={router.makeHashLink({ tag: "search" })}
            className={style.a_around_button}
            style={"width: 100%;"}
          >
            {dynClass(
              modeS,
              (mode) =>
                mode.tag === "search"
                  ? style.listleftlayout_left_item_active
                  : "",
              <div className={style.listleftlayout_left_item}>
                <i className={FA.icon_search}></i>
                <div style="font-style: italic">
                  {"Zoek of start nieuw gesprek"}
                </div>
              </div>,
            )}
          </a>

          {conversations.map((c) => {
            return dynVariadic(
              [modeS, c.unreadCountS],
              ([mode, unreadCount]) => {
                const selectionClass =
                  mode.tag === "selected" &&
                  counterpartyEq(
                    mode.counterparty,
                    conversationToCounterparty(c),
                  ) &&
                  counterpartyEq(c.own_identity, mode.own_identity)
                    ? style.listleftlayout_left_item_active
                    : "";
                return (
                  <div
                    className={style.listleftlayout_left_item + selectionClass}
                    style={unreadCount > 0 ? "padding-right: 40px;" : ""}
                    onclick={() => selectConversation(c)}
                  >
                    <i className={FA.icon_comment_alt_solid}></i>
                    {shouldShowOwnIdentity
                      ? (c.own_identity.tag === "Admin"
                          ? "Admin"
                          : c.own_identity.tag === "Teacher"
                          ? "Docent"
                          : c.own_identity.tag === "Student"
                          ? c.own_identity.name
                          : checkAllCasesHandled(c.own_identity)) + " - "
                      : ""}
                    {c.counterparty_admin
                      ? opts.schoolname
                      : c.counterparty_studentname ||
                        c.counterparty_teachername ||
                        ""}
                    {unreadCount > 0 ? (
                      <div className={style.listleftlayout_unread_badge}>
                        {unreadCount}
                      </div>
                    ) : (
                      <span></span>
                    )}
                  </div>
                );
              },
            );
          })}
          {showLoadMoreButton ? (
            <a
              href={routes.messagesRoute.link({
                showall: true,
                selected: { none: "none" },
              })}
              className={style.a_around_button}
            >
              <div className={style.listleftlayout_left_item}>
                <i className={FA.icon_plus_solid}></i>
                <div>{"Toon alle gesprekken"}</div>
              </div>
            </a>
          ) : (
            <span></span>
          )}
        </div>
      </aside>
      <div className={style.listleftlayout_middle}>
        <div className={style.listleftlayout_middle_max_width}>
          {dyn(modeS, (mode) => {
            if (mode.tag === "none") {
              return <span></span>;
            } else if (mode.tag === "search") {
              const searchS = new Source("");
              const resultsS: Source<{ link: string; name: string }[]> =
                new Source([]);

              async function recalcSearch_() {
                const searchstring = searchS.get().trim();
                if (searchstring === "") {
                  resultsS.set([]);
                } else {
                  const res = await rpc(
                    routes.searchCounterpartySpec,
                    undefined,
                    { searchstring },
                  );
                  resultsS.set(res);
                }
              }

              const recalcSearch = debounce(recalcSearch_, 200);

              scheduleForCleanup(searchS.observe(recalcSearch));

              const id = "classy_school_messages_search_box";

              setTimeout(() => {
                const el: null | HTMLElement = document.querySelector(`#${id}`);
                el?.focus();
              }, 1);

              return (
                <div>
                  {textbox({
                    id,
                    source: searchS,
                    placeholder: "Met wie wil je een gesprek starten?",
                    style: "background-color: white",
                  })}
                  <div className={style.grid_card_small}>
                    {dyn(resultsS, (results) =>
                      results.map((res) => (
                        <a href={res.link} className={style.card}>
                          {res.name}
                        </a>
                      )),
                    )}
                  </div>
                </div>
              );
            } else if (mode.tag === "selected") {
              const messagesS: Source<
                GetReturnTypeFromApiSpec<
                  typeof routes.getMessagesForConversationSpec
                >
              > = new Source([]);
              const conversation = conversations.find(
                (c) =>
                  counterpartyEq(
                    conversationToCounterparty(c),
                    mode.counterparty,
                  ) && counterpartyEq(c.own_identity, mode.own_identity),
              );
              if (!conversation) {
                return <div>{"Tegenpartij niet gevonden"}</div>;
              }

              rpc(routes.getMessagesForConversationSpec, undefined, {
                timestamp_cutoff: joda.LocalDateTime.now().minusYears(10), // todo
                counterparty: mode.counterparty,
                own_identity: mode.own_identity,
              })
                .then((mess) => {
                  messagesS.set(
                    mess.orderBy(
                      (m) =>
                        m.date.toEpochDay() * 100000 + m.time.toSecondOfDay(),
                    ),
                  );
                  setTimeout(() => {
                    const found = document.querySelector("textarea");
                    found?.scrollIntoView();
                  }, 1);
                })
                .catch(() => {});

              return dyn(messagesS, (messages) => (
                <div>
                  <div>
                    <a
                      onclick={() => history.back()}
                      className={style.listleftlayout_back_link}
                    >
                      &#60; {"Terug"}
                    </a>
                  </div>
                  <div>
                    {/* Rendering messages */}
                    {messages.map((m) => renderMessageChatBubble(m))}

                    {/* Counterparty links */}

                    <div style="padding: 8px;margin: 16px -8px 8px; display: flex; justify-content: flex-end">
                      {conversation.counterparty_studentid &&
                      conversation.counterparty_studentname ? (
                        <a
                          style="padding: 8px 16px;border-radius: 8px;background-color: white;"
                          href={routes.studentOverviewRoute.link({
                            studentid: conversation.counterparty_studentid,
                          })}
                        >
                          {conversation.counterparty_studentname}
                        </a>
                      ) : (
                        <span></span>
                      )}

                      {(() => {
                        const lastMessageReceived = messages
                          .filter((m) => m.tag === "received")
                          .last();
                        if (
                          lastMessageReceived &&
                          (opts.roleTag === "Admin" ||
                            opts.roleTag === "Teacher")
                        ) {
                          return dyn(
                            conversation.unreadCountS,
                            (unreadCount) => {
                              if (unreadCount === 0) {
                                return (
                                  <button
                                    className={style.text_with_icon}
                                    style="margin-left: 16px;"
                                    onclick={() =>
                                      rpc(routes.markAsUnreadSpec, undefined, {
                                        message_id: lastMessageReceived.id,
                                        own_identity: conversation.own_identity,
                                      }).then(() =>
                                        conversation.unreadCountS.set(1),
                                      )
                                    }
                                  >
                                    <i className={FA.icon_envelope_solid}></i>
                                    <div>
                                      {"Markeer als ongelezen"}
                                    </div>
                                  </button>
                                );
                              } else {
                                return <div></div>;
                              }
                            },
                          );
                        } else {
                          return <div></div>;
                        }
                      })()}
                    </div>

                    {/* rendering input */}
                    {renderTextarea(mode.counterparty, mode.own_identity, () =>
                      selectConversation(conversation, true),
                    )}
                  </div>
                </div>
              ));
            } else {
              return checkAllCasesHandled(mode);
            }
          })}
        </div>
      </div>
    </div>,
  );
}

function renderTextarea(
  counterparty: counterparty,
  own_identity: counterparty,
  reload: () => {},
) {
  const placeholder = "Schrijf je bericht...";
  const textare = (
    <textarea
      style="background-color: white"
      placeholder={placeholder || ""}
      onkeypress={(ev: KeyboardEvent) => {
        if (ev.key.toLowerCase() === "enter" && (ev.ctrlKey || ev.metaKey)) {
          ev.preventDefault();
          void send();
        }
      }}
    />
  ) as HTMLTextAreaElement;
  async function send() {
    const mess = textare.value;
    if (mess !== "") {
      await rpc(routes.sendMessageSpec, undefined, {
        counterparty: counterparty,
        message: mess,
        own_identity,
      });
      reload();
    }
  }
  return (
    <div className={style.messages_expanding_area}>
      {textare}
      <button
        style="bottom: 24px; right: 8px; position: absolute"
        className={style.button_accent}
        onclick={send}
      >
        {"Stuur"}
      </button>
    </div>
  );
}

function counterpartyEq(a: counterparty, b: counterparty) {
  if (a.tag === "Admin" && b.tag === "Admin") {
    return true;
  }
  if (
    a.tag === "Teacher" &&
    b.tag === "Teacher" &&
    a.teacherId === b.teacherId
  ) {
    return true;
  }
  if (
    a.tag === "Student" &&
    b.tag === "Student" &&
    a.studentId === b.studentId
  ) {
    return true;
  }
  return false;
}

export function conversationToCounterparty(c: conversation): counterparty {
  return c.counterparty_admin
    ? {
        tag: "Admin" as const,
      }
    : c.counterparty_teacherid
    ? { tag: "Teacher" as const, teacherId: c.counterparty_teacherid }
    : {
        tag: "Student" as const,
        studentId: c.counterparty_studentid!,
        name: c.counterparty_studentname || "",
      };
}

export type messageForChatBubble =
  | {
      tag: "received";
      id: messageid;
      othercounterpartyname: null | string; // counterparty was not us, and was not the counterparty
      date: joda.LocalDate;
      time: joda.LocalTime;
      stamp: joda.LocalDateTime;
      text: string;
      emailsent: boolean;
      read: boolean;
    }
  | {
      tag: "sent";
      id: messageid;
      othercounterpartyname: null | string; // counterparty was not us, and was not the counterparty
      date: joda.LocalDate;
      time: joda.LocalTime;
      stamp: joda.LocalDateTime;
      text: string;
      emailsent: boolean;
    };
export function renderMessageChatBubble(
  m: messageForChatBubble,
  opts?: { hideEmails?: boolean; forceEverythingRead: boolean },
) {
  return (
    <div>
      <div
        className={
          style.messages_message_container +
          (m.othercounterpartyname !== null
            ? style.messages_message_sent_by_other
            : m.tag === "sent"
            ? style.messages_message_sent_by_self
            : style.messages_message_sent_by_counterparty)
        }
      >
        <div
          style={
            "white-space: pre-wrap; word-break: break-word;" +
            (m.tag === "received" &&
            m.read === false &&
            m.othercounterpartyname === null
              ? opts?.forceEverythingRead === true
                ? ""
                : "font-weight: bold;"
              : "")
          }
        >
          {m.text}
        </div>
        {m.othercounterpartyname ? (
          <div className={style.messages_message_author}>
            {m.othercounterpartyname}
          </div>
        ) : (
          <span></span>
        )}
      </div>
      <div className={style.messages_message_meta}>
        {m.emailsent === false || opts?.hideEmails === true ? (
          <span></span>
        ) : (
          <a
            href={routes.messagesemailsRoute.link({
              messageid: m.id,
            })}
          >
            {"Bekijk email"}
          </a>
        )}
        <span className={style.messages_message_time}>
          {m.date.format(dateFormatWithYear)} {m.time.format(HHmm)}
        </span>
      </div>
    </div>
  );
}
